'use client';

import { createClient } from '@/lib/supabase/client';

export interface UserCredits {
  id: string;
  userId: string;
  freeSessionsUsed: number;
  purchasedSessions: number;
  sessionsRemaining: number;
  totalSessionsCompleted: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentTransaction {
  id: string;
  userId: string;
  stripeSessionId: string | null;
  packageName: string;
  sessionsGranted: number;
  amountCents: number;
  currency: string;
  paymentStatus: string;
  createdAt: string;
}

export const CREDIT_PACKAGES = [
  { id: 'starter', name: 'Starter', sessions: 5, price: 5, priceId: 'starter_5_5' },
  { id: 'basic', name: 'Basic', sessions: 10, price: 8, priceId: 'basic_10_8' },
  { id: 'standard', name: 'Standard', sessions: 15, price: 11, priceId: 'standard_15_11' },
  { id: 'premium', name: 'Premium', sessions: 25, price: 23, priceId: 'premium_25_23', popular: true },
  { id: 'professional', name: 'Professional', sessions: 50, price: 29, priceId: 'professional_50_29' },
] as const;

function mapCredits(row: Record<string, unknown>): UserCredits {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    freeSessionsUsed: (row.free_sessions_used as number) ?? 0,
    purchasedSessions: (row.purchased_sessions as number) ?? 0,
    sessionsRemaining: (row.sessions_remaining as number) ?? 0,
    totalSessionsCompleted: (row.total_sessions_completed as number) ?? 0,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export async function fetchUserCredits(
  userId: string,
  _accessToken?: string,
  _refreshToken?: string
): Promise<UserCredits | null> {
  try {
    const res = await fetch(`/api/credits?userId=${encodeURIComponent(userId)}`);
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try {
        const err = await res.json();
        errMsg = err.error || errMsg;
      } catch {
        try {
          errMsg = await res.text() || errMsg;
        } catch {}
      }
      console.error('[Credits] fetchUserCredits error:', errMsg);
      return null;
    }
    const { data } = await res.json();
    if (!data) return null;
    return mapCredits(data as Record<string, unknown>);
  } catch (err) {
    console.error('[Credits] fetchUserCredits network error:', err);
    return null;
  }
}

export async function decrementSessionCredit(
  userId: string,
  accessToken?: string,
  refreshToken?: string,
  creditCost: number = 1
): Promise<boolean> {
  // Fetch current credits
  const credits = await fetchUserCredits(userId, accessToken, refreshToken);
  if (!credits) return false;
  if (credits.sessionsRemaining < creditCost) return false;

  const isFreeSession = credits.freeSessionsUsed < 2 && credits.purchasedSessions === 0;

  // For fractional credits (0.5), we deduct from sessions_remaining as a decimal
  // The DB stores as numeric so fractional values are supported
  const updates: Record<string, number> = {
    sessions_remaining: Math.max(0, credits.sessionsRemaining - creditCost),
    total_sessions_completed: credits.totalSessionsCompleted + 1,
  };

  if (isFreeSession) {
    updates.free_sessions_used = credits.freeSessionsUsed + 1;
  }

  try {
    const res = await fetch('/api/credits', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, updates }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[Credits] decrementSessionCredit error:', err.error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('[Credits] decrementSessionCredit network error:', err);
    return false;
  }
}

export async function allocateCredits(
  userId: string,
  sessions: number,
  packageName: string,
  stripeSessionId: string,
  amountCents: number
): Promise<boolean> {
  const supabase = createClient();

  // Upsert credits
  const { data: existing } = await supabase
    .from('user_credits')
    .select('sessions_remaining, purchased_sessions')
    .eq('user_id', userId)
    .single();

  if (existing) {
    const { error } = await supabase
      .from('user_credits')
      .update({
        purchased_sessions: (existing.purchased_sessions ?? 0) + sessions,
        sessions_remaining: (existing.sessions_remaining ?? 0) + sessions,
      })
      .eq('user_id', userId);
    if (error) {
      console.error('[Credits] allocateCredits update error:', error.message);
      return false;
    }
  } else {
    const { error } = await supabase
      .from('user_credits')
      .insert({
        user_id: userId,
        purchased_sessions: sessions,
        sessions_remaining: sessions,
        free_sessions_used: 0,
        total_sessions_completed: 0,
      });
    if (error) {
      console.error('[Credits] allocateCredits insert error:', error.message);
      return false;
    }
  }

  // Record transaction
  await supabase.from('payment_transactions').insert({
    user_id: userId,
    stripe_session_id: stripeSessionId,
    package_name: packageName,
    sessions_granted: sessions,
    amount_cents: amountCents,
    currency: 'usd',
    payment_status: 'succeeded',
  });

  return true;
}

export async function fetchPaymentHistory(
  userId: string,
  accessToken?: string,
  refreshToken?: string
): Promise<PaymentTransaction[]> {
  const supabase = createClient();
  if (accessToken && refreshToken) {
    try {
      await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
    } catch {}
  }

  const { data, error } = await supabase
    .from('payment_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) return [];

  return (data ?? []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    userId: row.user_id as string,
    stripeSessionId: row.stripe_session_id as string | null,
    packageName: row.package_name as string,
    sessionsGranted: row.sessions_granted as number,
    amountCents: row.amount_cents as number,
    currency: row.currency as string,
    paymentStatus: row.payment_status as string,
    createdAt: row.created_at as string,
  }));
}
